import * as React from 'react';
import { isEmpty } from 'lodash';

import {
  IControlError,
  IControlSchema,
  IControlSchemaBase,
  ValidationResult,
} from '../../core/Controls';

import { IDispatcher } from '../helpers/Dispatcher';
import { cn, Component, I18NString } from '../helpers/Utils';

import Field from './components/Field';
import ErrorAlert from './components/ErrorAlert';

export interface ICheckboxSchema extends IControlSchemaBase {
  value?: string;
  disabled?: boolean;
  description: I18NString;
  notification: I18NString;
  extra?: any;
}

interface ICheckboxProps {
  schema: ICheckboxSchema;
  api: any;
  dispatcher?: IDispatcher;
  error: IControlError | undefined;
  visited: boolean;
}

interface ICheckboxState {
  isOpen: boolean;
  isOpenNotification: boolean;
}

class Checkbox extends Component<ICheckboxProps, ICheckboxState> {
  private checkbox: HTMLInputElement;

  state = {
    isOpen: false,
    isOpenNotification: true,
  };

  componentDidMount() {
    const {
      schema: { value, disabled },
      error,
    } = this.props;
    if (error) {
      return;
    }
    const validationResult = this.validationResult(value);
    if (!disabled) {
      this.props.dispatcher.onBlur(
        this.props.schema.id,
        value,
        !validationResult,
        validationResult,
      );
    }
  }

  onChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    const value = this.checkbox.checked ? '1' : null;

    const validationResult = this.validationResult(value);
    this.props.dispatcher.onBlur(
      this.props.schema.id,
      value,
      !validationResult,
      validationResult,
    );
  };

  onClick = () => {
    this.setState(({ isOpen }) => ({
      isOpen: !isOpen,
    }));
  };

  onOpenNotification = () => {
    this.setState(({ isOpenNotification }) => ({
      isOpenNotification: !isOpenNotification,
    }));
  };

  // eslint-disable-next-line class-methods-use-this
  validationResult(_value: string): ValidationResult | undefined {
    return undefined;
  }

  render() {
    const {
      schema: {
        disabled,
        description,
        notification,
        value,
        extra,
        title,
        required,
      },
      visited,
      error,
    } = this.props;
    const errorText = visited && error && error.message;

    return (
      <Field
        {...this.props.schema}
        title={null}
        status={isEmpty(value) ? 'default' : 'success'}
      >
        {extra && extra.hint && (
          <div
            className="alert alert-info hint"
            dangerouslySetInnerHTML={{ __html: extra.hint }}
          />
        )}
        <div className="input-group-cr input-group-cr--nowrap">
          <label className={cn('order-wrapper', { disabled })}>
            <input
              ref={(node) => {
                this.checkbox = node;
              }}
              type="checkbox"
              disabled={disabled}
              checked={value === '1'}
              onChange={this.onChange}
            />
            <div
              className={cn('reportbox-content-desc', { required })}
              dangerouslySetInnerHTML={{ __html: this.tr(title) }}
            />
          </label>
          {notification && !disabled && (
            <span className="btn btn-default" onClick={this.onOpenNotification}>
              <span className="glyphicon glyphicon-info-sign" />
            </span>
          )}
          {description && !disabled && !description.defaultOpen && (
            <span className="btn btn-default" onClick={this.onClick}>
              <span className="glyphicon glyphicon-question-sign" />
            </span>
          )}
        </div>
        {notification && this.state.isOpenNotification && (
          <div className="alert alert-warning" role="alert">
            <span
              dangerouslySetInnerHTML={{
                __html: this.props.schema?.notification?.ru,
              }}
            />
          </div>
        )}
        {description && (this.state.isOpen || description.defaultOpen) && (
          <div
            className="alert alert-warning"
            role="alert"
            dangerouslySetInnerHTML={{ __html: this.tr(description) }}
          />
        )}
        <ErrorAlert errorMessage={errorText} />
      </Field>
    );
  }
}

export function createCheckbox(
  schema: IControlSchema,
  api: any,
  dispatcher: IDispatcher = null,
  error: IControlError | undefined,
  visited: boolean,
): React.ReactElement<any> {
  return (
    <Checkbox
      schema={schema as ICheckboxSchema}
      api={api}
      dispatcher={dispatcher}
      error={error}
      visited={visited}
    />
  );
}
